import { useMemo, useState, useCallback } from 'react';
import { type ColumnDef } from '@tanstack/react-table';
import { LeaderboardTable } from './LeaderboardTable';
import { ChartContainer } from '../shared/ChartContainer';
import { EmptyState } from '../shared/EmptyState';
import { ErrorState } from '../shared/ErrorState';
import { LeaderboardSkeleton } from '../shared/AnalyticsSkeletons';
import { ExportButton } from '../shared/ExportButton';
import { PIIExportDialog } from '../shared/PIIExportDialog';
import { useCSVExport } from '../../../hooks/useCSVExport';
import { useToast } from '@/shared/components/Toast';
import type { FAAdvisorRow, SortOrder } from '../../../types/analytics.types';

type FAClientLeaderboardProps = {
  advisors: FAAdvisorRow[];
  totalCount: number;
  search: string;
  setSearch: (value: string) => void;
  sortBy: string;
  sortOrder: SortOrder;
  handleSort: (column: string) => void;
  showPendingInvitations: boolean;
  setShowPendingInvitations: (value: boolean) => void;
  isLoading: boolean;
  isError: boolean;
  hasNextPage: boolean;
  isFetchingNextPage: boolean;
  fetchNextPage: () => void;
  onRetry?: () => void;
  exportParams?: Record<string, string | number | boolean | undefined>;
};

const RankBadge = ({ rank }: { rank: number }) => (
  <div className="w-8 h-8 rounded-full bg-[#3d6560] text-white flex items-center justify-center text-sm font-medium">
    {rank}
  </div>
);

const ClientList = ({ names }: { names: string[] }) => {
  if (names.length === 0) return <span className="text-gray-400">—</span>;
  const display = names.slice(0, 3);
  const remainder = names.length - 3;
  return (
    <div className="text-sm">
      {display.join(', ')}
      {remainder > 0 && (
        <span className="text-gray-400"> +{remainder} more</span>
      )}
    </div>
  );
};

export const FAClientLeaderboard = ({
  advisors,
  totalCount,
  search,
  setSearch,
  sortBy,
  sortOrder,
  handleSort,
  showPendingInvitations,
  setShowPendingInvitations,
  isLoading,
  isError,
  hasNextPage,
  isFetchingNextPage,
  fetchNextPage,
  onRetry,
  exportParams,
}: FAClientLeaderboardProps) => {
  const [showPIIDialog, setShowPIIDialog] = useState(false);
  const { showToast } = useToast();

  const handleExportError = useCallback(
    (message: string) => {
      showToast(`Export failed: ${message}. Please try again.`, 'error', 5000);
    },
    [showToast],
  );

  const { exportCSV, isExporting } = useCSVExport({ onError: handleExportError });

  const handleExportClick = useCallback(() => {
    setShowPIIDialog(true);
  }, []);

  const handleExportConfirm = useCallback(async () => {
    await exportCSV('fa-clients', exportParams);
    setShowPIIDialog(false);
  }, [exportCSV, exportParams]);
  const columns = useMemo<ColumnDef<FAAdvisorRow, unknown>[]>(() => {
    const base: ColumnDef<FAAdvisorRow, unknown>[] = [
      {
        id: 'rank',
        header: '#',
        enableSorting: false,
        cell: ({ row }) => <RankBadge rank={row.index + 1} />,
        size: 60,
      },
      {
        id: 'faName',
        accessorKey: 'faName',
        header: 'FA Name',
        cell: ({ getValue }) => (
          <span className="font-medium text-gray-900">
            {getValue() as string}
          </span>
        ),
      },
      {
        id: 'partnerFirm',
        accessorKey: 'partnerFirm',
        header: 'Partner Firm',
        cell: ({ getValue }) => (
          <span className="inline-flex items-center px-2.5 py-1 rounded-full text-xs font-medium bg-[#e8f4f2] text-[#3d6560]">
            {getValue() as string}
          </span>
        ),
      },
      {
        id: 'totalClients',
        accessorKey: 'totalClients',
        header: 'Total Clients',
        cell: ({ getValue }) => (
          <span className="font-semibold text-[#3d6560]">
            {getValue() as number}
          </span>
        ),
      },
    ];

    if (showPendingInvitations) {
      base.push({
        id: 'pendingInvitations',
        accessorKey: 'pendingInvitations',
        header: 'Pending',
        cell: ({ getValue }) => {
          const val = getValue() as number;
          return val > 0 ? (
            <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-amber-100 text-amber-800">
              {val}
            </span>
          ) : (
            <span className="text-gray-400">0</span>
          );
        },
      });
    }

    base.push(
      {
        id: 'totalReferrals',
        accessorKey: 'totalReferrals',
        header: 'Total Referrals',
        cell: ({ getValue }) => (getValue() as number).toString(),
      },
      {
        id: 'clientNames',
        header: 'Client Names',
        enableSorting: false,
        cell: ({ row }) => (
          <ClientList
            names={row.original.clients.map((c) => c.clientName)}
          />
        ),
      },
      {
        id: 'clientEmails',
        header: 'Client Emails',
        enableSorting: false,
        cell: ({ row }) => (
          <ClientList
            names={row.original.clients.map((c) => c.clientEmail)}
          />
        ),
      },
    );

    return base;
  }, [showPendingInvitations]);

  if (isLoading) return <LeaderboardSkeleton />;

  if (isError) {
    return (
      <ChartContainer
        title="Financial Advisor Client Leaderboard"
        subtitle="FA client activity and referrals"
      >
        <ErrorState
          message="Failed to load FA client data. Please try again."
          onRetry={onRetry}
        />
      </ChartContainer>
    );
  }

  if (advisors.length === 0) {
    return (
      <ChartContainer
        title="Financial Advisor Client Leaderboard"
        subtitle="FA client activity and referrals"
      >
        <EmptyState message="No financial advisor data found for the selected filters." />
      </ChartContainer>
    );
  }

  return (
    <>
      <ChartContainer
        title="Financial Advisor Client Leaderboard"
        subtitle="FA client activity and referrals"
        action={
          <div className="flex items-center gap-4">
            <label className="flex items-center gap-2 text-sm cursor-pointer">
              <input
                type="checkbox"
                checked={showPendingInvitations}
                onChange={(e) => setShowPendingInvitations(e.target.checked)}
                className="rounded border-gray-300 text-[#3d6560] focus:ring-[#3d6560]"
                aria-label="Show Pending Invitations"
              />
              <span className="text-gray-600">Show Pending Invitations</span>
            </label>
            <ExportButton
              onClick={handleExportClick}
              isLoading={isExporting}
              label="Export CSV"
            />
          </div>
        }
      >
        <LeaderboardTable
          data={advisors}
          columns={columns}
          search={search}
          onSearchChange={setSearch}
          searchPlaceholder="Search by advisor or firm..."
          sortBy={sortBy}
          sortOrder={sortOrder}
          onSort={handleSort}
          totalCount={totalCount}
          countLabel="advisors"
          hasNextPage={hasNextPage}
          isFetchingNextPage={isFetchingNextPage}
          onLoadMore={fetchNextPage}
        />
      </ChartContainer>
      <PIIExportDialog
        open={showPIIDialog}
        onOpenChange={setShowPIIDialog}
        onConfirm={handleExportConfirm}
        exportType="FA Clients"
        isLoading={isExporting}
      />
    </>
  );
};
